import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { OrgWidgetData } from '../../models/org-widget.models';
import { OrgStructureFlowDialogComponent } from './org-structure-flow-dialog.component';
import { OrgDialogService } from '../../../org-board/services/org-dialog.service';
import { OrgEntityColorsService } from '../../services/org-entity-colors.service';

interface NodeDetail {
    icon: string;
    iconColor: string;
    name: string;
    type: 'hat' | 'user' | 'security-group' | 'policy';
    key: string;
}

interface HierarchyNode {
    name: string;
    partKey: string;
    depth: number;
    children: HierarchyNode[];
    bgColor?: string;
    textColor?: string;
    partType?: string;
    expanded: boolean;
    detailsHidden: boolean;
    details: NodeDetail[];
    matchesSearch: boolean;
    hasMatchingDescendant: boolean;
}

type ShowToLevel = 'all' | 'executive' | 'division' | 'department' | 'section' | 'unit' | 'post';

const PART_TYPE_DEPTH: Record<string, number> = {
    executive: 0,
    division: 1,
    department: 2,
    section: 3,
    unit: 4,
    post: 5,
};

@Component({
    selector: 'shared-org-hierarchy-widget',
    imports: [
        CommonModule,
        MatIconModule,
        MatDialogModule,
        MatTooltipModule,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
    ],
    template: `
        <!-- Search Bar -->
        <div class="search-bar">
            <mat-icon class="search-icon">search</mat-icon>
            <input
                type="text"
                [(ngModel)]="searchTerm"
                (ngModelChange)="onSearchChange()"
                placeholder="Search org posts, hats, staff..."
                class="search-input"
            />
            <button *ngIf="searchTerm" class="clear-btn" (click)="clearSearch()">
                <mat-icon>close</mat-icon>
            </button>
        </div>

        <!-- Toolbar: Expand/Collapse + Show-to filters -->
        <div class="toolbar">
            <div class="toolbar-actions">
                <button class="toolbar-btn expand-btn" (click)="expandAll()" matTooltip="Expand All">
                    <mat-icon>unfold_more</mat-icon>
                    <span>Expand</span>
                </button>
                <button class="toolbar-btn collapse-btn" (click)="collapseAll()" matTooltip="Collapse All">
                    <mat-icon>unfold_less</mat-icon>
                    <span>Collapse</span>
                </button>
                <button
                    class="toolbar-btn help-btn"
                    (click)="openOrgStructureFlow()"
                    matTooltip="How the org structure works"
                >
                    <mat-icon>help_outline</mat-icon>
                    <span>How it works</span>
                </button>
            </div>
            <div class="toolbar-filters">
                <span class="filter-label">Show to:</span>
                <button
                    *ngFor="let level of showToLevels"
                    class="filter-chip"
                    [class.active]="showToLevel === level.value"
                    (click)="setShowToLevel(level.value)"
                >
                    {{ level.label }}
                </button>
            </div>
        </div>

        <div class="hierarchy-container" *ngIf="tree.length > 0; else emptyState">
            <ng-container *ngTemplateOutlet="nodeTemplate; context: { $implicit: tree, level: 0 }"></ng-container>
        </div>

        <ng-template #nodeTemplate let-nodes let-level="level">
            <div *ngFor="let node of nodes" class="tree-node" [class.hidden-node]="isNodeHidden(node)">
                <div
                    class="node-row"
                    [style.padding-left.px]="level * 16 + 4"
                    [class.has-children]="node.children?.length > 0"
                    [class.search-match]="searchTerm && node.matchesSearch"
                >
                    <!-- Expand/collapse toggle -->
                    <button
                        class="toggle-btn"
                        *ngIf="node.children?.length > 0 || node.details?.length > 0"
                        (click)="toggleExpand(node)"
                    >
                        <mat-icon>{{ node.expanded ? 'expand_more' : 'chevron_right' }}</mat-icon>
                    </button>
                    <span class="toggle-spacer" *ngIf="!node.children?.length && !node.details?.length"></span>

                    <!-- Color badge for the position (clickable) -->
                    <span
                        class="part-badge clickable"
                        [style.background]="node.bgColor || '#78909c'"
                        [style.color]="node.textColor || '#fff'"
                        (click)="onPositionClick(node, $event)"
                    >
                        <mat-icon class="part-icon part-icon-grey">
                            {{ node.partType === 'post' ? 'person' : 'folder' }}
                        </mat-icon>
                        {{ node.name }}
                    </span>

                    <span class="part-type-label" *ngIf="node.partType">{{ node.partType }}</span>

                    <!-- Detail count badge -->
                    <span
                        class="detail-count"
                        *ngIf="node.details?.length > 0"
                        (click)="toggleDetails(node); $event.stopPropagation()"
                    >
                        <mat-icon class="detail-count-icon">info_outline</mat-icon>
                        {{ node.details.length }}
                    </span>
                </div>

                <!-- Collapsible details (hats, users, security groups, policies) -->
                <div
                    *ngIf="node.expanded && !node.detailsHidden && node.details?.length > 0"
                    class="node-details"
                    [style.padding-left.px]="level * 16 + 40"
                >
                    <div
                        *ngFor="let detail of node.details"
                        class="detail-row clickable"
                        [class.search-match]="searchTerm && isDetailMatch(detail)"
                        (click)="onDetailClick(detail, $event)"
                    >
                        <mat-icon class="detail-icon" [style.color]="detail.iconColor">{{ detail.icon }}</mat-icon>
                        <span class="detail-name">{{ detail.name }}</span>
                        <span
                            class="detail-type-chip"
                            [style.color]="getChipColor(detail.type)"
                            [style.background]="getChipBackground(detail.type)"
                            >{{ getDetailTypeLabel(detail.type) }}</span
                        >
                    </div>
                </div>

                <!-- Children -->
                <ng-container *ngIf="node.expanded && node.children?.length > 0">
                    <ng-container
                        *ngTemplateOutlet="nodeTemplate; context: { $implicit: node.children, level: level + 1 }"
                    >
                    </ng-container>
                </ng-container>
            </div>
        </ng-template>

        <ng-template #emptyState>
            <div class="empty-state">
                <mat-icon>account_tree</mat-icon>
                <span>No org structure defined</span>
            </div>
        </ng-template>
    `,
    styles: [
        `
            :host {
                display: flex;
                flex-direction: column;
                height: 100%;
                overflow: hidden;
            }

            .search-bar {
                display: flex;
                align-items: center;
                gap: 6px;
                padding: 6px 8px;
                border-bottom: 1px solid rgba(0, 0, 0, 0.08);
                flex-shrink: 0;
            }

            .search-icon {
                font-size: 18px;
                width: 18px;
                height: 18px;
                color: rgba(0, 0, 0, 0.38);
                flex-shrink: 0;
            }

            .search-input {
                flex: 1;
                border: none;
                outline: none;
                font-size: 13px;
                background: transparent;
                color: inherit;
                min-width: 0;
            }

            .clear-btn {
                background: none;
                border: none;
                cursor: pointer;
                padding: 0;
                display: flex;
                align-items: center;
                color: rgba(0, 0, 0, 0.38);
                mat-icon {
                    font-size: 16px;
                    width: 16px;
                    height: 16px;
                }
            }

            /* ─── Toolbar ─── */
            .toolbar {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 4px 8px;
                border-bottom: 1px solid rgba(0, 0, 0, 0.06);
                flex-shrink: 0;
                flex-wrap: wrap;
            }

            .toolbar-actions {
                display: flex;
                gap: 2px;
            }

            .toolbar-btn {
                display: inline-flex;
                align-items: center;
                gap: 2px;
                background: none;
                border: 1px solid rgba(0, 0, 0, 0.12);
                border-radius: 4px;
                cursor: pointer;
                padding: 2px 6px;
                font-size: 11px;
                color: rgba(0, 0, 0, 0.7);
                transition: background 0.15s;

                &:hover {
                    background: rgba(0, 0, 0, 0.06);
                }
                mat-icon {
                    font-size: 14px;
                    width: 14px;
                    height: 14px;
                }
            }

            .help-btn {
                color: #e65100;
                border-color: rgba(230, 81, 0, 0.3);
                &:hover {
                    background: rgba(230, 81, 0, 0.08);
                }
            }

            .toolbar-filters {
                display: flex;
                align-items: center;
                gap: 3px;
                margin-left: auto;
            }

            .filter-label {
                font-size: 10px;
                color: rgba(0, 0, 0, 0.38);
                white-space: nowrap;
            }

            .filter-chip {
                background: none;
                border: 1px solid rgba(0, 0, 0, 0.12);
                border-radius: 10px;
                cursor: pointer;
                padding: 1px 7px;
                font-size: 10px;
                color: rgba(0, 0, 0, 0.6);
                transition: all 0.15s;

                &:hover {
                    background: rgba(0, 0, 0, 0.06);
                }
                &.active {
                    background: #1976d2;
                    color: #fff;
                    border-color: #1976d2;
                }
            }

            /* ─── Tree ─── */
            .hierarchy-container {
                overflow-y: auto;
                padding: 4px 0;
                flex: 1;
                scrollbar-width: thin;
                scrollbar-color: rgba(0, 0, 0, 0.2) transparent;

                &::-webkit-scrollbar {
                    width: 6px;
                }
                &::-webkit-scrollbar-track {
                    background: transparent;
                    border-radius: 3px;
                }
                &::-webkit-scrollbar-thumb {
                    background: rgba(0, 0, 0, 0.2);
                    border-radius: 3px;
                    &:hover {
                        background: rgba(0, 0, 0, 0.3);
                    }
                }
            }

            .tree-node {
                margin-bottom: 1px;
            }
            .hidden-node {
                display: none;
            }

            .node-row {
                display: flex;
                align-items: center;
                gap: 4px;
                padding: 3px 4px;
                border-radius: 4px;
                transition: background 0.15s ease;

                &:hover {
                    background: rgba(0, 0, 0, 0.04);
                }
                &.search-match {
                    background: rgba(255, 235, 59, 0.15);
                }
            }

            .toggle-btn {
                background: none;
                border: none;
                cursor: pointer;
                padding: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                width: 20px;
                height: 20px;
                flex-shrink: 0;
                color: rgba(0, 0, 0, 0.54);
                border-radius: 3px;

                &:hover {
                    background: rgba(0, 0, 0, 0.08);
                }
                mat-icon {
                    font-size: 18px;
                    width: 18px;
                    height: 18px;
                }
            }

            .toggle-spacer {
                width: 20px;
                flex-shrink: 0;
            }

            .part-badge.clickable {
                cursor: pointer;
                &:hover {
                    filter: brightness(1.15);
                }
            }
            .part-badge {
                display: inline-flex;
                align-items: center;
                gap: 4px;
                padding: 2px 8px 2px 4px;
                border-radius: 4px;
                font-size: 12px;
                font-weight: 500;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                min-width: 0;
            }

            .part-icon {
                font-size: 14px;
                width: 14px;
                height: 14px;
                flex-shrink: 0;
            }

            .part-icon-grey {
                color: rgba(200, 200, 200, 0.7) !important;
            }

            .part-type-label {
                font-size: 10px;
                color: rgba(0, 0, 0, 0.38);
                background: rgba(0, 0, 0, 0.04);
                padding: 1px 6px;
                border-radius: 10px;
                white-space: nowrap;
                flex-shrink: 0;
                margin-left: 4px;
            }

            /* ─── Detail count badge ─── */
            .detail-count {
                display: inline-flex;
                align-items: center;
                gap: 2px;
                font-size: 10px;
                color: rgba(0, 0, 0, 0.38);
                cursor: pointer;
                margin-left: auto;
                padding: 1px 4px;
                border-radius: 8px;
                transition: background 0.15s;

                &:hover {
                    background: rgba(0, 0, 0, 0.06);
                }
            }

            .detail-count-icon {
                font-size: 13px;
                width: 13px;
                height: 13px;
            }

            /* ─── Details panel ─── */
            .node-details {
                padding: 2px 0 4px;
                border-left: 2px solid rgba(0, 0, 0, 0.08);
                margin-left: 10px;
            }

            .detail-row {
                display: flex;
                align-items: center;
                gap: 6px;
                padding: 2px 8px;
                border-radius: 3px;

                &.clickable {
                    cursor: pointer;
                    &:hover {
                        background: rgba(0, 0, 0, 0.04);
                    }
                }
                &.search-match {
                    background: rgba(255, 235, 59, 0.15);
                }
            }

            .detail-icon {
                font-size: 14px;
                width: 14px;
                height: 14px;
                flex-shrink: 0;
            }

            .detail-name {
                font-size: 12px;
                color: rgba(0, 0, 0, 0.7);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                flex: 1;
                min-width: 0;
            }

            .detail-type-chip {
                font-size: 9px;
                padding: 0px 5px;
                border-radius: 8px;
                white-space: nowrap;
                flex-shrink: 0;

                /* chip colors now set dynamically via [style] bindings from colorsService */
            }

            .empty-state {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 8px;
                padding: 24px;
                color: rgba(0, 0, 0, 0.38);

                mat-icon {
                    font-size: 36px;
                    width: 36px;
                    height: 36px;
                }
                span {
                    font-size: 13px;
                }
            }

            /* ─── Dark theme ─── */
            :host-context(.dark-theme) {
                .search-bar {
                    border-bottom-color: rgba(255, 255, 255, 0.08);
                }
                .search-icon {
                    color: rgba(255, 255, 255, 0.38);
                }
                .search-input {
                    color: rgba(255, 255, 255, 0.87);
                }
                .clear-btn {
                    color: rgba(255, 255, 255, 0.38);
                }

                .toolbar {
                    border-bottom-color: rgba(255, 255, 255, 0.06);
                }
                .toolbar-btn {
                    border-color: rgba(255, 255, 255, 0.12);
                    color: rgba(255, 255, 255, 0.7);
                    &:hover {
                        background: rgba(255, 255, 255, 0.08);
                    }
                }
                .filter-label {
                    color: rgba(255, 255, 255, 0.38);
                }
                .filter-chip {
                    border-color: rgba(255, 255, 255, 0.12);
                    color: rgba(255, 255, 255, 0.6);
                    &:hover {
                        background: rgba(255, 255, 255, 0.08);
                    }
                    &.active {
                        background: #1976d2;
                        color: #fff;
                        border-color: #1976d2;
                    }
                }

                .node-row:hover {
                    background: rgba(255, 255, 255, 0.06);
                }
                .node-row.search-match,
                .detail-row.search-match {
                    background: rgba(255, 235, 59, 0.08);
                }
                .toggle-btn {
                    color: rgba(255, 255, 255, 0.54);
                    &:hover {
                        background: rgba(255, 255, 255, 0.08);
                    }
                }
                .part-type-label {
                    color: rgba(255, 255, 255, 0.38);
                    background: rgba(255, 255, 255, 0.06);
                }
                .detail-count {
                    color: rgba(255, 255, 255, 0.38);
                    &:hover {
                        background: rgba(255, 255, 255, 0.08);
                    }
                }
                .node-details {
                    border-left-color: rgba(255, 255, 255, 0.08);
                }
                .detail-row.clickable:hover {
                    background: rgba(255, 255, 255, 0.06);
                }
                .detail-name {
                    color: rgba(255, 255, 255, 0.7);
                }
                /* detail-type-chip colors now set dynamically via [style] bindings from colorsService */
                .empty-state {
                    color: rgba(255, 255, 255, 0.38);
                }
                .hierarchy-container {
                    scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
                    &::-webkit-scrollbar-thumb {
                        background: rgba(255, 255, 255, 0.2);
                        &:hover {
                            background: rgba(255, 255, 255, 0.3);
                        }
                    }
                }
            }
        `,
    ],
})
export class OrgHierarchyWidgetComponent implements OnChanges {
    @Input() data!: OrgWidgetData;
    public tree: HierarchyNode[] = [];
    public searchTerm = '';
    public showToLevel: ShowToLevel = 'all';

    constructor(
        private dialog: MatDialog,
        private dialogService: OrgDialogService,
        private colorsService: OrgEntityColorsService
    ) {}

    public showToLevels: { label: string; value: ShowToLevel }[] = [
        { label: 'All', value: 'all' },
        { label: 'Exec', value: 'executive' },
        { label: 'Div', value: 'division' },
        { label: 'Dept', value: 'department' },
        { label: 'Sec', value: 'section' },
        { label: 'Unit', value: 'unit' },
        { label: 'Post', value: 'post' },
    ];

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['data'] && this.data) {
            this.buildTree();
        }
    }

    public toggleExpand(node: HierarchyNode): void {
        node.expanded = !node.expanded;
    }

    public toggleDetails(node: HierarchyNode): void {
        node.detailsHidden = !node.detailsHidden;
        if (!node.detailsHidden && !node.expanded) {
            node.expanded = true;
        }
    }

    public expandAll(): void {
        this.setExpansion(this.tree, true);
    }

    public collapseAll(): void {
        this.setExpansion(this.tree, false);
    }

    public setShowToLevel(level: ShowToLevel): void {
        this.showToLevel = level;
        if (level === 'all') {
            // Expand to depth 2 by default
            this.resetExpansion(this.tree);
        } else {
            this.expandToLevel(this.tree, PART_TYPE_DEPTH[level] ?? 99);
        }
    }

    public isNodeHidden(node: HierarchyNode): boolean {
        if (this.searchTerm) {
            return !node.matchesSearch && !node.hasMatchingDescendant;
        }
        return false;
    }

    public onSearchChange(): void {
        const term = this.searchTerm.toLowerCase().trim();
        if (!term) {
            this.resetSearchFlags(this.tree);
            return;
        }
        this.applySearch(this.tree, term);
    }

    public clearSearch(): void {
        this.searchTerm = '';
        this.resetSearchFlags(this.tree);
    }

    public isDetailMatch(detail: NodeDetail): boolean {
        return this.searchTerm ? detail.name.toLowerCase().includes(this.searchTerm.toLowerCase().trim()) : false;
    }

    public getDetailTypeLabel(type: string): string {
        switch (type) {
            case 'hat':
                return 'Hat';
            case 'user':
                return 'User';
            case 'security-group':
                return 'SG';
            case 'policy':
                return 'Policy';
            default:
                return type;
        }
    }

    /** Map detail type ('hat','user','security-group','policy') → OrgEntityType */
    private detailTypeToEntityType(type: string): 'hats' | 'users' | 'securityGroups' | 'policies' {
        switch (type) {
            case 'hat':
                return 'hats';
            case 'user':
                return 'users';
            case 'security-group':
                return 'securityGroups';
            case 'policy':
                return 'policies';
            default:
                return 'users';
        }
    }

    public getChipColor(type: string): string {
        return this.colorsService.getColor(this.detailTypeToEntityType(type));
    }

    public getChipBackground(type: string): string {
        return this.colorsService.getBackground(this.detailTypeToEntityType(type));
    }

    private setExpansion(nodes: HierarchyNode[], expanded: boolean): void {
        for (const node of nodes) {
            node.expanded = expanded;
            node.detailsHidden = !expanded;
            this.setExpansion(node.children, expanded);
        }
    }

    private resetExpansion(nodes: HierarchyNode[]): void {
        for (const node of nodes) {
            node.expanded = node.depth < 2;
            this.resetExpansion(node.children);
        }
    }

    private expandToLevel(nodes: HierarchyNode[], maxDepth: number): void {
        for (const node of nodes) {
            const nodeTypeDepth = PART_TYPE_DEPTH[node.partType || ''] ?? node.depth;
            node.expanded = nodeTypeDepth <= maxDepth;
            this.expandToLevel(node.children, maxDepth);
        }
    }

    private applySearch(nodes: HierarchyNode[], term: string): boolean {
        let anyMatch = false;
        for (const node of nodes) {
            const nameLower = node.name.toLowerCase();
            const typeLower = (node.partType || '').toLowerCase();
            const detailMatch = node.details.some((d) => d.name.toLowerCase().includes(term));
            node.matchesSearch = nameLower.includes(term) || typeLower.includes(term) || detailMatch;

            const childMatch = this.applySearch(node.children, term);
            node.hasMatchingDescendant = childMatch;

            if (node.matchesSearch || node.hasMatchingDescendant) {
                node.expanded = true;
                if (detailMatch) node.detailsHidden = false;
                anyMatch = true;
            }
        }
        return anyMatch;
    }

    private resetSearchFlags(nodes: HierarchyNode[]): void {
        for (const node of nodes) {
            node.matchesSearch = false;
            node.hasMatchingDescendant = false;
            node.expanded = node.depth < 2;
            node.detailsHidden = false;
            this.resetSearchFlags(node.children);
        }
    }

    private buildTree(): void {
        const parts = this.data.positions || [];
        if (!parts.length) {
            this.tree = [];
            return;
        }

        // Build detail maps by position partKey
        const detailsByPartKey = new Map<string, NodeDetail[]>();

        const addDetail = (pk: string, detail: NodeDetail): void => {
            if (!detailsByPartKey.has(pk)) detailsByPartKey.set(pk, []);
            const list = detailsByPartKey.get(pk);
            if (list && !list.some((d) => d.key === detail.key && d.type === detail.type)) {
                list.push(detail);
            }
        };

        // Build lookup maps for indirect linking
        const userMap = new Map<string, { name: string; key: string }>();
        for (const user of this.data.users || []) {
            const d = user.data as Record<string, unknown>;
            if (!d) continue;
            const userName = (d['displayName'] || d['name'] || d['userName'] || d['email'] || 'Unnamed User') as string;
            userMap.set(user.key, { name: userName, key: user.key });
            // Also index by uid if present
            const uid = d['uid'] as string;
            if (uid) userMap.set(uid, { name: userName, key: user.key });
        }

        const policyMap = new Map<string, { name: string; key: string }>();
        for (const pol of this.data.policies || []) {
            const d = pol.data as Record<string, unknown>;
            if (!d) continue;
            const polName = (d['name'] || d['title'] || 'Unnamed Policy') as string;
            policyMap.set(pol.key, { name: polName, key: pol.key });
        }

        // Hats → link to positions, and also resolve users & policies through hats
        for (const hat of this.data.hats || []) {
            const d = hat.data as Record<string, unknown>;
            if (!d) continue;
            const hatName = (d['name'] || 'Unnamed Hat') as string;
            const orgPos = d['orgPosition'] as Record<string, unknown> | undefined;
            const positionArray = d['positionArray'] as string[] | undefined;
            const positions = d['positions'] as Array<Record<string, unknown>> | undefined;

            // Collect all partKeys this hat is linked to
            const partKeys = new Set<string>();
            if (orgPos?.['partKey']) partKeys.add(orgPos['partKey'] as string);
            if (positionArray) positionArray.forEach((pk) => partKeys.add(pk));
            if (positions) {
                for (const pos of positions) {
                    if (pos['partKey']) partKeys.add(pos['partKey'] as string);
                }
            }

            for (const pk of partKeys) {
                // Add the hat itself
                addDetail(pk, {
                    icon: 'school',
                    iconColor: this.colorsService.getColor('hats'),
                    name: hatName,
                    type: 'hat',
                    key: hat.key,
                });

                // Users linked through this hat (assignedToArray or userKeys)
                const assignedTo = (d['assignedToArray'] || d['userKeys']) as string[] | undefined;
                if (assignedTo) {
                    for (const userIdOrKey of assignedTo) {
                        const usr = userMap.get(userIdOrKey);
                        if (usr) {
                            addDetail(pk, {
                                icon: 'person',
                                iconColor: this.colorsService.getColor('users'),
                                name: usr.name,
                                type: 'user',
                                key: usr.key,
                            });
                        }
                    }
                }

                // Policies linked through this hat (policyKeys)
                const policyKeys = d['policyKeys'] as Record<string, string> | undefined;
                if (policyKeys) {
                    for (const polKey of Object.keys(policyKeys)) {
                        const pol = policyMap.get(polKey);
                        if (pol) {
                            addDetail(pk, {
                                icon: 'policy',
                                iconColor: this.colorsService.getColor('policies'),
                                name: pol.name,
                                type: 'policy',
                                key: pol.key,
                            });
                        }
                    }
                }
            }
        }

        // Security Groups → link directly via orgPosition.partKey
        for (const sg of this.data.securityGroups || []) {
            const d = sg.data as Record<string, unknown>;
            if (!d) continue;
            const sgName = (d['name'] || 'Unnamed SG') as string;
            const orgPos = d['orgPosition'] as Record<string, unknown> | undefined;
            if (orgPos?.['partKey']) {
                addDetail(orgPos['partKey'] as string, {
                    icon: 'security',
                    iconColor: this.colorsService.getColor('securityGroups'),
                    name: sgName,
                    type: 'security-group',
                    key: sg.key,
                });
            }
        }

        // Policies → also check direct part targeting via targets.parts[]
        for (const pol of this.data.policies || []) {
            const d = pol.data as Record<string, unknown>;
            if (!d) continue;
            const polName = (d['name'] || d['title'] || 'Unnamed Policy') as string;
            const targets = d['targets'] as Record<string, unknown> | undefined;
            const targetParts = targets?.['parts'] as Array<Record<string, unknown>> | undefined;
            if (targetParts) {
                for (const tp of targetParts) {
                    const pk = tp['key'] as string;
                    if (pk) {
                        addDetail(pk, {
                            icon: 'policy',
                            iconColor: this.colorsService.getColor('policies'),
                            name: polName,
                            type: 'policy',
                            key: pol.key,
                        });
                    }
                }
            }
        }

        // Build node map
        const nodeMap = new Map<string, HierarchyNode>();
        for (const part of parts) {
            const p = part as Record<string, unknown>;
            const colour = p['colour'] as Record<string, string> | undefined;
            const partKey = (p['partKey'] || p['id']) as string;
            const depth = (p['depth'] || 0) as number;

            nodeMap.set(partKey, {
                name: (p['name'] || p['mainTitle'] || 'Unnamed') as string,
                partKey,
                depth,
                children: [],
                bgColor: colour?.['background'] || undefined,
                textColor: colour?.['color'] || undefined,
                partType: p['partType'] as string | undefined,
                expanded: depth < 2,
                detailsHidden: false,
                details: detailsByPartKey.get(partKey) || [],
                matchesSearch: false,
                hasMatchingDescendant: false,
            });
        }

        // Link children to parents
        const roots: HierarchyNode[] = [];
        for (const part of parts) {
            const p = part as Record<string, unknown>;
            const key = (p['partKey'] || p['id']) as string;
            const node = nodeMap.get(key);
            if (!node) continue;
            const parentKey = p['parentPartKey'] as string;

            const parentNode = parentKey ? nodeMap.get(parentKey) : undefined;
            if (parentNode) {
                parentNode.children.push(node);
            } else {
                roots.push(node);
            }
        }

        this.tree = roots;
    }

    public onPositionClick(node: HierarchyNode, event: MouseEvent): void {
        event.stopPropagation();
        const parts = this.data.positions || [];
        const part = parts.find((p) => {
            const rec = p as Record<string, unknown>;
            return (rec['partKey'] || rec['id']) === node.partKey;
        });
        if (part) {
            this.dialogService.openPositionDetailsDialog(part);
        }
    }

    public onDetailClick(detail: NodeDetail, event: MouseEvent): void {
        event.stopPropagation();
        switch (detail.type) {
            case 'user': {
                const userEntity = (this.data.users || []).find((u) => u.key === detail.key);
                const userData = userEntity
                    ? { ...userEntity.data, key: detail.key, name: detail.name }
                    : { key: detail.key, name: detail.name };
                this.dialogService.openOrgUserDetailsDialog(null as any, userData);
                break;
            }
            case 'hat': {
                const hatEntity = (this.data.hats || []).find((h) => h.key === detail.key);
                if (hatEntity) {
                    this.dialogService.openHatDetailsDialog(detail.key, hatEntity.data);
                }
                break;
            }
            case 'security-group': {
                const sgEntity = (this.data.securityGroups || []).find((sg) => sg.key === detail.key);
                if (sgEntity) {
                    this.dialogService.openSecurityGroupDetailsDialog(detail.key, sgEntity.data);
                }
                break;
            }
            case 'policy': {
                const policyEntity = (this.data.policies || []).find((p) => p.key === detail.key);
                if (policyEntity) {
                    this.dialogService.openPolicyDetailsDialog(detail.key, policyEntity.data);
                }
                break;
            }
        }
    }

    openOrgStructureFlow(): void {
        this.dialog.open(OrgStructureFlowDialogComponent, {
            panelClass: 'security-flow-dialog-panel',
            width: '92vw',
            height: '92vh',
            maxWidth: '98vw',
            maxHeight: '98vh',
            autoFocus: false,
        });
    }
}
